import { useCallback } from "react";

import { config } from "../config";
import { useEventsApi } from "./useEventsApi";
import type {
  ActivityListResponse,
  CreatorSession,
  CreatorSessionListResponse,
  CreatorSessionStatus,
  PendingApprovalEvent,
} from "../shared/workspace";
import type { EventAuditLogRow } from "../shared/eventAuditLog";

type SaveSessionPayload = {
  title: string;
  status?: CreatorSessionStatus;
  payload?: Record<string, unknown>;
};

export const useCreatorWorkspaceApi = () => {
  const { callApi } = useEventsApi();

  const listMySessions = useCallback(async (limit = 20, offset = 0): Promise<CreatorSessionListResponse> => {
    return (await callApi(
      `${config.apiUrl}/api/me/sessions/?limit=${limit}&offset=${offset}`,
      "GET"
    )) as CreatorSessionListResponse;
  }, [callApi]);

  const createSession = useCallback(
    async (payload: SaveSessionPayload): Promise<CreatorSession> => {
      return (await callApi(`${config.apiUrl}/api/me/sessions/`, "POST", payload)) as CreatorSession;
    },
    [callApi]
  );

  const updateSession = useCallback(
    async (id: number, payload: Partial<SaveSessionPayload>): Promise<CreatorSession> => {
      return (await callApi(`${config.apiUrl}/api/me/sessions/${id}/`, "PATCH", payload)) as CreatorSession;
    },
    [callApi]
  );

  const deleteSession = useCallback(
    async (id: number): Promise<void> => {
      await callApi(`${config.apiUrl}/api/me/sessions/${id}/`, "DELETE");
    },
    [callApi]
  );

  const getMyActivity = useCallback(
    async (limit = 50, offset = 0): Promise<ActivityListResponse> => {
      return (await callApi(
        `${config.apiUrl}/api/me/activity/?limit=${limit}&offset=${offset}`,
        "GET"
      )) as ActivityListResponse;
    },
    [callApi]
  );

  const getMyEventActivity = useCallback(
    async (limit = 25, offset = 0): Promise<{ count: number; offset: number; limit: number; total: number; results: EventAuditLogRow[] }> => {
      return (await callApi(
        `${config.apiUrl}/api/me/event-activity/?limit=${limit}&offset=${offset}`,
        "GET"
      )) as { count: number; offset: number; limit: number; total: number; results: EventAuditLogRow[] };
    },
    [callApi]
  );

  const getAdminActivity = useCallback(
    async (params?: { userUuid?: string; limit?: number; offset?: number }): Promise<ActivityListResponse> => {
      const q = new URLSearchParams();
      q.set("limit", String(params?.limit ?? 50));
      q.set("offset", String(params?.offset ?? 0));
      if (params?.userUuid?.trim()) {
        q.set("user_uuid", params.userUuid.trim());
      }
      return (await callApi(`${config.apiUrl}/api/admin/user-activity/?${q.toString()}`, "GET")) as ActivityListResponse;
    },
    [callApi]
  );

  const getMyPendingApprovals = useCallback(
    async (limit = 25, offset = 0): Promise<{ count: number; offset: number; limit: number; total: number; results: PendingApprovalEvent[] }> => {
      return (await callApi(
        `${config.apiUrl}/api/me/pending-approvals/?limit=${limit}&offset=${offset}`,
        "GET"
      )) as { count: number; offset: number; limit: number; total: number; results: PendingApprovalEvent[] };
    },
    [callApi]
  );

  return {
    listMySessions,
    createSession,
    updateSession,
    deleteSession,
    getMyActivity,
    getMyEventActivity,
    getMyPendingApprovals,
    getAdminActivity,
  };
};
